import { useEffect, useMemo, useRef, useState } from "react";
import { Polyline, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Car, Clock, Zap, CalendarDays, Radio, Flag, Leaf } from "lucide-react";
import { co2SavedKg } from "../lib/co2";
import { tNL } from "../lib/journalI18n";

const distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const carIcon = L.divIcon({
  className: "",
  html: `<div class="vr-progress-car"><span class="vr-progress-pulse"></span><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const fmtH = (h, suf = "u") => {
  const m = Math.round((h || 0) * 60);
  return m % 60 === 0 ? `${m / 60}${suf}` : `${Math.floor(m / 60)}${suf}${String(m % 60).padStart(2, "0")}`;
};

export const DrivenRoute = ({ geometry, progress, animate = true, liveOverride = null }) => {
  const lineRef = useRef(null);
  const [drawn, setDrawn] = useState(!animate);
  const driven = useMemo(() => {
    if (!progress?.started || !geometry || geometry.length < 2) return { pts: [], km: null };
    if (progress.finished) return { pts: geometry, km: null };
    const cum = [0];
    for (let i = 1; i < geometry.length; i++) cum.push(cum[i - 1] + distKm(geometry[i - 1], geometry[i]));
    const geomLen = cum[cum.length - 1] || 1;
    if (liveOverride && liveOverride.idx > 0) {
      const idx = Math.min(liveOverride.idx, geometry.length - 1);
      return { pts: geometry.slice(0, idx + 1), km: progress.total_km > 0 ? cum[idx] * (progress.total_km / geomLen) : null };
    }
    if (!progress.position || progress.percent <= 0 || !progress.driven_km) return { pts: [], km: null };
    if (Number.isInteger(progress.position_geo_idx) && progress.position_geo_idx > 0) {
      const idx = Math.min(progress.position_geo_idx, geometry.length - 1);
      return { pts: geometry.slice(0, idx + 1), km: null };
    }
    const target = progress.total_km > 0 ? progress.driven_km * (geomLen / progress.total_km) : progress.driven_km;
    let end = geometry.length - 1;
    for (let i = 1; i < cum.length; i++) {
      if (cum[i] >= target) { end = i; break; }
    }
    return { pts: geometry.slice(0, end + 1), km: null };
  }, [geometry, progress, liveOverride]);

  useEffect(() => {
    if (!animate || driven.pts.length < 2 || driven.pts.length > 4000) { setDrawn(true); return; }
    let raf;
    const t0 = performance.now();
    const dur = 2000;
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const n = Math.max(2, Math.ceil(driven.pts.length * eased));
      if (lineRef.current) lineRef.current.setLatLngs(driven.pts.slice(0, n));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDrawn(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [driven, animate]);

  const carPos = liveOverride?.pos || progress?.position;
  if (!progress?.started || !carPos) return null;
  const shownKm = driven.km != null ? driven.km : progress.driven_km;
  const shownPct = driven.km != null && progress.total_km > 0 ? Math.min(Math.round((driven.km / progress.total_km) * 100), 100) : progress.percent;
  return (
    <>
      {driven.pts.length >= 2 && (
        <Polyline ref={lineRef} positions={animate ? [driven.pts[0]] : driven.pts}
          pathOptions={{ color: "#16A34A", weight: 6, opacity: 0.95, lineCap: "round" }} />
      )}
      {(drawn || driven.pts.length < 2) && (
        <Marker position={[carPos.lat, carPos.lon]} icon={carIcon} zIndexOffset={2500}>
          <Popup>
            <strong>{progress.finished ? "Reis voltooid! 🎉" : "Hier zijn we"}</strong><br />
            {Math.round(shownKm).toLocaleString("nl-BE")} van {progress.total_km.toLocaleString("nl-BE")} km gereden ({shownPct}%)
            {!progress.finished && progress.eta && <><br /><em>nog {Math.round(progress.eta.remaining_km ?? 0)} km · aankomst ±{new Date(progress.eta.time).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })} bij {progress.eta.place}</em></>}
            {liveOverride ? <><br /><em>o.b.v. live GPS-positie</em></> : progress.source === "live" && <><br /><em>o.b.v. laatste live-positie{progress.position_ts ? ` · ${new Date(progress.position_ts).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}` : ""}</em></>}
          </Popup>
        </Marker>
      )}
    </>
  );
};

export const TripProgressPanel = ({ progress, overlay = false, corner = "bottom-right", moto = false, compact = false, liveStatus = null, t = tNL, locale = "nl-BE" }) => {
  const [fill, setFill] = useState(0);
  useEffect(() => {
    if (!progress) return;
    const t = setTimeout(() => setFill(progress.percent), 150);
    return () => clearTimeout(t);
  }, [progress]);
  if (!progress?.started) return null;
  return (
    <div data-testid="trip-progress-panel"
      className={overlay
        ? `absolute ${corner === "top-right" ? "top-14" : "bottom-4 lg:bottom-32"} right-3 z-[1000] w-[290px] max-w-[calc(100%-24px)] rounded-2xl bg-white/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.18)] border border-white/60 p-4`
        : `bg-card rounded-2xl ${compact ? "p-3.5" : "p-4"} border border-[#16A34A]/25`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#16A34A] flex items-center gap-1.5">
          <Car className="w-3.5 h-3.5" /> {progress.finished ? t("tripDone") : t("routeProgress")}
        </p>
        {progress.source === "live" && !progress.finished && (
          <span data-testid="progress-source-live" className="flex items-center gap-1 text-[9px] font-bold uppercase text-red-500">
            <Radio className="w-3 h-3 animate-pulse" /> live
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span data-testid="progress-driven-km" className="font-heading font-extrabold text-xl leading-none">{Math.round(progress.driven_km).toLocaleString(locale)}</span>
        <span className="text-xs text-muted-foreground font-semibold">{t("ofDriven", { km: Math.round(progress.total_km).toLocaleString(locale) })}</span>
        <span data-testid="progress-percent" className="ml-auto font-mono font-bold text-sm text-[#16A34A]">{progress.percent}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-3">
        <div data-testid="progress-bar-fill" className="h-full rounded-full bg-gradient-to-r from-[#2D6A4F] to-[#16A34A] transition-[width] duration-[1500ms] ease-out"
          style={{ width: `${fill}%` }} />
      </div>
      <div className={`grid ${moto ? "grid-cols-3 gap-2" : "grid-cols-4 gap-1.5"} text-center`}>
        <div className="rounded-xl bg-secondary/60 py-1.5 px-1">
          <p className="text-[9px] uppercase tracking-wide font-bold text-muted-foreground flex items-center justify-center gap-1"><CalendarDays className="w-3 h-3" /> {t("rowDay")}</p>
          <p data-testid="progress-days" className="font-mono font-bold text-sm">{progress.finished ? progress.days_total : Math.min(progress.days_done + 1, progress.days_total)}<span className="text-muted-foreground font-normal">/{progress.days_total}</span></p>
        </div>
        <div className="rounded-xl bg-secondary/60 py-1.5 px-1">
          <p className="text-[9px] uppercase tracking-wide font-bold text-muted-foreground flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> {t("rowDrive")}</p>
          <p data-testid="progress-hours" className="font-mono font-bold text-xs leading-5 truncate">{fmtH(progress.drive_hours_done, t("hourSuffix"))}<span className="text-muted-foreground font-normal">/{fmtH(progress.drive_hours_total, t("hourSuffix"))}</span></p>
        </div>
        <div className="rounded-xl bg-secondary/60 py-1.5 px-1">
          <p className="text-[9px] uppercase tracking-wide font-bold text-muted-foreground flex items-center justify-center gap-1"><Zap className="w-3 h-3" /> {t("rowStops")}</p>
          <p data-testid="progress-stops" className="font-mono font-bold text-sm">{progress.charge_stops_done}<span className="text-muted-foreground font-normal">/{progress.charge_stops_total}</span></p>
        </div>
        {!moto && (
        <div className="rounded-xl bg-[#2D6A4F]/10 py-1.5 px-1" title={t("co2Tip")}>
          <p className="text-[9px] uppercase tracking-wide font-bold text-[#2D6A4F] flex items-center justify-center gap-1"><Leaf className="w-3 h-3" /> CO₂</p>
          <p data-testid="progress-co2" className="font-mono font-bold text-sm text-[#2D6A4F]">-{co2SavedKg(progress.driven_km)}<span className="font-normal text-[10px]"> kg</span></p>
        </div>
        )}
      </div>
      {progress.eta && !progress.finished && !(liveStatus && progress.position) && (
        <p data-testid="progress-eta" className="mt-2.5 text-xs font-bold text-[#16A34A] bg-[#16A34A]/10 rounded-full py-1.5 px-3 flex items-center justify-center gap-1.5">
          <Flag className="w-3 h-3" /> {t("expectedAround", { time: new Date(progress.eta.time).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" }) })}{progress.eta.place ? t("inPlace", { place: progress.eta.place }) : ""}
        </p>
      )}
      {liveStatus}
    </div>
  );
};
